import { Flex } from "@100mslive/hms-ai-ui";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useMeasure } from "react-use";
import type { CallFlow } from "src/types/journey";

import type { RootState } from "../../../store";
import { formattedDate, getLocalZone } from "../../../utils/time";
import { CopyButton } from "../container/copyButton";

const JourneyContext = ({ journey }: { journey: CallFlow }) => {
  const callFlowId = journey?.id || "-";
  const createdAt = formattedDate(journey?.created_at || "", false, false);
  const updatedAt = formattedDate(journey?.updated_at || "", false, false);
  const name = useSelector(
    (state: RootState) => state.journey.journey.display_name,
  );
  const timeZone = getLocalZone();
  const [isExpanded, setIsExpanded] = useState(false);
  const [containerRef, containerMeasures] = useMeasure<HTMLDivElement>();
  const formattedDescription = journey.description?.replace(/\\n/g, "\n") || "";

  const descriptionWidth = containerMeasures.width * 0.7;

  return (
    <Flex direction="column">
      <p className="text-txt-primary mb-2 text-2xl font-semibold">{name}</p>

      {formattedDescription && (
        <div className="w-full" ref={containerRef}>
          {isExpanded ? (
            <div className="text-sm text-txt-quaternary">
              <ReactMarkdown
                components={{
                  a: ({ node: _node, ...props }) => (
                    <a
                      {...props}
                      className="text-txt-brand-secondary underline"
                      target="_blank"
                    />
                  ),
                  p: ({ node: _node, ...props }) => (
                    <p
                      {...props}
                      className="text-sm text-txt-quaternary mb-2"
                    />
                  ),
                }}
              >
                {formattedDescription}
              </ReactMarkdown>
              <span
                className="text-txt-brand-tertiary ml-1 cursor-pointer"
                onClick={() => setIsExpanded(prevState => !prevState)}
              >
                ...less
              </span>
            </div>
          ) : (
            <div className="text-sm text-txt-quaternary w-full flex items-baseline">
              <span
                style={{ maxWidth: `${descriptionWidth}px` }}
                className="overflow-hidden whitespace-nowrap"
              >
                {journey.description}
              </span>
              <span
                className="text-txt-brand-tertiary ml-1 cursor-pointer flex-shrink-0"
                onClick={() => setIsExpanded(prevState => !prevState)}
              >
                ...more
              </span>
            </div>
          )}
        </div>
      )}

      <Flex justify="between" className="py-4">
        <Card title="Call Flow ID" value={callFlowId} showCopyIcon />

        <Flex className="gap-8">
          <Card title={`Updated At (${timeZone})`} value={updatedAt} />
          <Card title={`Started At (${timeZone})`} value={createdAt} />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default JourneyContext;

const Card = ({
  title,
  value,
  showCopyIcon,
}: {
  title: string;
  value: string;
  showCopyIcon?: boolean;
}) => {
  return (
    <Flex direction="column">
      <p className="text-sm text-txt-quaternary">{title}</p>
      <Flex align="center" className="gap-2">
        <p className="text-sm text-txt-secondary max-w-40 truncate">{value}</p>
        {showCopyIcon ? (
          <CopyButton content={value === "-" ? "" : value} />
        ) : (
          <></>
        )}
      </Flex>
    </Flex>
  );
};
